import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, FileText, CheckSquare, Folder } from 'lucide-react';
import { axiosInstance } from '../App';
import { useNavigate } from 'react-router-dom';

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search function with debounce
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/search', {
          params: { q: query, type: selectedType }
        });
        setResults(response.data.results);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query, selectedType]);

  // Handle result click
  const handleResultClick = (result) => {
    if (result.type === 'project') {
      navigate(`/project/${result.id}`);
    } else if (result.type === 'task') {
      // Find project ID from result
      navigate(`/project/${result.project_id || ''}`);
    }
    setIsOpen(false);
    setQuery('');
  };

  // Get icon for result type
  const getIcon = (type) => {
    switch (type) {
      case 'project':
        return <Folder className="w-4 h-4" />;
      case 'task':
        return <CheckSquare className="w-4 h-4" />;
      case 'document':
        return <FileText className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">{t('search')}</span>
        <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-xs font-medium border rounded bg-gray-100 dark:bg-gray-800">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={() => setIsOpen(false)}
      />

      {/* Search Modal */}
      <div className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl z-50 px-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              placeholder={t('searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 outline-none bg-transparent text-base"
              autoFocus
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Type Filter */}
          <div className="flex gap-2 p-3 border-b overflow-x-auto">
            {['all', 'project', 'task', 'document'].map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors ${
                  selectedType === type
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {t(type)}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="p-8 text-center text-gray-500">
                {t('search')}...
              </div>
            )}

            {!loading && query && results.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {t('noResults')}
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="p-2">
                {Object.entries(groupedResults).map(([type, items]) => (
                  <div key={type} className="mb-4">
                    <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase">
                      {t(type + 's')}
                    </div>
                    {items.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className="w-full flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md text-left transition-colors"
                      >
                        <div className="mt-1 text-gray-400">
                          {getIcon(result.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.title}</div>
                          {result.description && (
                            <div className="text-sm text-gray-500 truncate">
                              {result.description}
                            </div>
                          )}
                          {result.project_name && (
                            <div className="text-xs text-gray-400 mt-1">
                              {result.project_name}
                            </div>
                          )}
                        </div>
                        {result.status && (
                          <div className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800">
                            {t(result.status)}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-3 border-t text-xs text-gray-500">
            <span>Toplam: {results.length} sonuç</span>
            <span>ESC ile kapat</span>
          </div>
        </div>
      </div>
    </>
  );
}
